import { Component, Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { forkJoin, of } from 'rxjs';
import { switchMap, } from 'rxjs/operators';
import { IMenu, IMenuGroup } from '../../../interfaces/menu';
import { TranslateService } from '@ngx-translate/core';

type MenuLanguage = string | null;

type MenuForm = {
  name: FormControl<string>;
  language: FormControl<MenuLanguage>;
  hidden: FormControl<boolean>;
  menuFile: FormControl<File | null>;
  iconFile: FormControl<File | null>;
  bannerFile: FormControl<File | null>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-menu',
  templateUrl: './add-menu.component.html',
  styleUrls: ['./add-menu.component.scss'],
})
export class AddMenuComponent {
  public isLoading = false;
  public selected = 0;
  public languages = [
    { value: 'en-CA', viewValue: 'English' },
    { value: 'fr-CA', viewValue: 'French' },
    { value: null, viewValue: 'Not Set' },
  ];
  public happyHours: boolean;
  public menusFormArray: FormArray<FormGroup<MenuForm>>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      restaurantId: number;
      happyHours: boolean;
      hidden?: boolean;
      mode?: 'create' | 'copy';
      source?: IMenuGroup;
    },
    private menuState: MenuStoreService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {
    const sourceMenus = this.data.source
      ? [this.data.source, ...this.data.source.translation]
      : [];

    this.happyHours = this.data.source?.happyHours ?? this.data.happyHours;
    this.menusFormArray = new FormArray<FormGroup<MenuForm>>(
      sourceMenus.length
        ? sourceMenus.map(menu => this.createMenuFormGroup(menu))
        : [this.createMenuFormGroup()]
    );
  }

  get isCopyMode(): boolean {
    return this.data.mode === 'copy';
  }

  get menusControls(): Array<FormGroup<MenuForm>> {
    return this.menusFormArray.controls;
  }

  get selectedLanguage(): MenuLanguage {
    return this.menusControls[this.selected].controls.language.value;
  }

  public isLanguageAvailable(language: MenuLanguage, currentIndex: number): boolean {
    const selectedLanguages = this.menusControls
      .map((menu, index) => index !== currentIndex ? menu.controls.language.value : null)
      .filter(lang => lang !== null);

    return !selectedLanguages.includes(language) && !(this.menusControls.length > 1 && language === null);
  }

  public addTranslate(): void {
    this.menusFormArray.push(this.createMenuFormGroup(undefined, true));

    this.selected = this.menusFormArray.length - 1;
  }

  public removeTranslate(index: number): void {
    this.menusFormArray.removeAt(index);
    this.selected = index;
  }

  onSubmit(): void {
    if (this.menusFormArray.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;

    const [menuOriginalData, ...menuTranslatedData] = this.menusControls;

    const addMenu = (menuControl: FormGroup<MenuForm>, originalId: number | null) => {
      const menuData = menuControl.getRawValue();

      if (!menuData.menuFile) {
        throw new Error('Menu file is required');
      }

      return this.menuState.addMenu(
        this.data.restaurantId,
        menuData.name,
        menuData.language,
        this.happyHours,
        menuData.hidden,
        menuData.menuFile,
        originalId,
        menuData.iconFile || undefined,
        menuData.bannerFile || undefined
      );
    };

    addMenu(menuOriginalData, null).pipe(
      switchMap((menu) => {
        if (menuTranslatedData.length > 0) {
          return forkJoin(
            menuTranslatedData.map((translate) => addMenu(translate, menu.id))
          );
        }
        return of(menu);
      }),
      untilDestroyed(this)
    ).subscribe({
      next: () => (
        this.isLoading = false,
        this.dialog.closeAll()
      ),
      error: () => (
        this.isLoading = false,
        this.dialog.closeAll()
      )
    });
  }

  private createMenuFormGroup(source?: IMenu, languageRequired = false): FormGroup<MenuForm> {
    const copySuffix = this.translate.instant('pages.menu.modal.copySuffix');

    return new FormGroup<MenuForm>({
      name: new FormControl(source ? `${source.name} (${copySuffix})` : '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(3)]
      }),
      language: new FormControl<MenuLanguage>(source?.language ?? null, languageRequired ? [Validators.required] : []),
      hidden: new FormControl(source?.hidden ?? false, { nonNullable: true }),
      menuFile: new FormControl<File | null>(null, [Validators.required]),
      iconFile: new FormControl<File | null>(null),
      bannerFile: new FormControl<File | null>(null)
    });
  }
}
